import api from "@/api";

export default {
    namespaced: true,
    state: () => ({
        gradeSheets: [],
        currentGradeSheet: null,
        isLoading: false,
        error: null,
        pagination: {
            currentPage: 1,
            totalPages: 1,
            totalItems: 0,
            perPage: 10,
            from: 0,
            to: 0
        }
    }),
    mutations: {
        SET_GRADE_SHEETS(state, gradeSheets) {
            state.gradeSheets = gradeSheets;
        },
        SET_CURRENT_GRADE_SHEET(state, gradeSheet) {
            state.currentGradeSheet = gradeSheet;
        },
        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },
        SET_ERROR(state, error) {
            state.error = error;
        },
        SET_PAGINATION(state, { currentPage, totalPages, totalItems, perPage, from, to }) {
            state.pagination = {
                currentPage,
                totalPages,
                totalItems,
                perPage,
                from,
                to
            };
        },
        ADD_GRADE_SHEET(state, gradeSheet) {
            state.gradeSheets.unshift(gradeSheet);
        },
        UPDATE_GRADE_SHEET(state, updatedGradeSheet) {
            const index = state.gradeSheets.findIndex(g => g.id === updatedGradeSheet.id);
            if (index !== -1) {
                state.gradeSheets.splice(index, 1, updatedGradeSheet);
            }
        },
        DELETE_GRADE_SHEET(state, gradeSheetId) {
            state.gradeSheets = state.gradeSheets.filter(g => g.id !== gradeSheetId);
        },
        RESET_CURRENT_GRADE_SHEET(state) {
            state.currentGradeSheet = null;
        }
    },
    actions: {
        async fetchGradeSheets({ commit }, { page = 1, per_page = 10, search = '', class_id = null, subject_id = null, academic_year_id = null } = {}) {
            try {
                commit('SET_LOADING', true);
                const response = await api.get('/grade-sheets', {
                    params: { 
                        page, 
                        per_page, 
                        search, 
                        class_id, 
                        subject_id, 
                        academic_year_id 
                    }
                });
                
                commit('SET_GRADE_SHEETS', response.data.data);
                commit('SET_PAGINATION', {
                    currentPage: response.data.meta.current_page,
                    totalPages: response.data.meta.last_page,
                    totalItems: response.data.meta.total,
                    perPage: response.data.meta.per_page,
                    from: response.data.meta.from,
                    to: response.data.meta.to
                });
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
      async fetchGradeSheet({ commit }, gradeSheetId) {
    try {
        commit('SET_LOADING', true);
        const response = await api.get(`/grade-sheets/${gradeSheetId}`, {
            params: { 
                include: 'class,subject,user,academicYear,structures.contents.student,structures.contents.teacher' 
            }
        });
        
        const gradeSheetData = response.data.data;
        if (!gradeSheetData) throw new Error('Empty response from server');
        
        commit('SET_CURRENT_GRADE_SHEET', gradeSheetData);
        return gradeSheetData;
    } catch (error) {
        commit('SET_ERROR', error.response?.data?.message || error.message);
        throw error;
    } finally {
        commit('SET_LOADING', false);
    }
},
        
        async createGradeSheet({ commit }, gradeSheetData) {
            try {
                commit('SET_LOADING', true);
                const response = await api.post('/grade-sheets', gradeSheetData);
                commit('ADD_GRADE_SHEET', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
        async updateGradeSheet({ commit }, { gradeSheetId, gradeSheetData }) {
            try {
                commit('SET_LOADING', true);
                const response = await api.put(`/grade-sheets/${gradeSheetId}`, gradeSheetData);
                commit('UPDATE_GRADE_SHEET', response.data);
                commit('SET_CURRENT_GRADE_SHEET', response.data);
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
        async deleteGradeSheet({ commit }, gradeSheetId) {
            if (!gradeSheetId) {
                throw new Error('Grade sheet ID is required');
            }

            try {
                commit('SET_LOADING', true);
                const response = await api.delete(`/grade-sheets/${gradeSheetId}`);
                
                if (response.status === 200) {
                    commit('DELETE_GRADE_SHEET', gradeSheetId);
                    commit('RESET_CURRENT_GRADE_SHEET');
                    return response.data;
                }
                
            } catch (error) {
                if (error.response?.status === 404) {
                    commit('SET_ERROR', 'Grade sheet not found');
                } else {
                    commit('SET_ERROR', error.response?.data?.message || error.message);
                }
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
        async updateGradeContent({ commit }, { structureId, contentData }) {
            try {
                commit('SET_LOADING', true);
                const response = await api.post(
                    `/grade-sheets/structures/${structureId}/update-content`,
                    contentData
                );
                return response.data;
            } catch (error) {
                commit('SET_ERROR', error.response?.data?.message || error.message);
                throw error;
            } finally {
                commit('SET_LOADING', false);
            }
        },
        
        clearError({ commit }) {
            commit('SET_ERROR', null);
        }
    },
    getters: {
        getGradeSheets: state => state.gradeSheets,
        getGradeSheetById: state => id => state.gradeSheets.find(g => g.id === id),
        getCurrentGradeSheet: state => state.currentGradeSheet,
        isLoading: state => state.isLoading,
        getError: state => state.error,
        getPagination: state => state.pagination
    }
};